import React, { useMemo } from 'react';
import useIsMobile from '@/shared/lib/hooks/useIsMobile';

/**
 * Pagination
 * @param {number} page         – текущая страница (0-based)
 * @param {number} totalPages   – общее число страниц
 * @param {Function} onPageChange – коллбэк смены страницы
 */
export default function Pagination({ page, totalPages, onPageChange }) {
    const isMobile = useIsMobile();

    /* ---------- helpers ---------- */

    const clamp = (p) => Math.max(0, Math.min(p, totalPages - 1));

    const handleClick = (newPage) => {
        onPageChange(clamp(newPage));
    };

    /**
     * формируем «умный» массив для пагинации
     * вариант: first … prev current next … last
     */
    const pages = useMemo(() => {
        /** Сколько «соседей» показываем вокруг current */
        const siblings = 1;
        /** Минимум элементов (first, last, current, 2×siblings, 2×dots) */
        const visibleSlots = 5 + siblings * 2;

        // 1) если страниц влезает меньше, чем visibleSlots, показываем все
        if (totalPages <= visibleSlots) {
            return Array.from({ length: totalPages }, (_, i) => i);
        }

        // 2) рассчитываем границы «окна»
        const left = Math.max(1, page - siblings);
        const right = Math.min(totalPages - 2, page + siblings);

        const showLeftDots = left > 2;                   // есть разрыв слева?
        const showRightDots = right < totalPages - 3;    // разрыв справа?

        const range = [0]; // всегда первая страница

        if (showLeftDots) range.push('dots-l');

        // middle pages
        for (let i = left; i <= right; i++) range.push(i);

        if (showRightDots) range.push('dots-r');

        range.push(totalPages - 1); // всегда последняя страница
        return range;
    }, [page, totalPages]);

    /* ---------- render ---------- */
    if (totalPages <= 1) return null; // ничего не рисуем, если страниц ≤ 1

    const Button = ({ disabled, children, onClick }) => (
        <button
            className="page-link"
            disabled={disabled}
            onClick={onClick}
            type="button"
        >
            {children}
        </button>
    );

    return (
        <nav className="d-flex justify-content-center mt-3">
            <ul className="pagination">

                {/* ← / Назад */}
                <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                    <Button disabled={page === 0} onClick={() => handleClick(page - 1)}>
                        {isMobile ? '←' : 'Назад'}
                    </Button>
                </li>

                {/* desktop-only номера страниц */}
                {!isMobile && pages.map((p, idx) => (
                    <li
                        key={`${p}-${idx}`}           // уникальный ключ ⇒ никакой «грязи» в DOM
                        className={`page-item ${p === page ? 'active' : ''} ${typeof p === 'string' ? 'disabled' : ''}`}
                    >
                        {typeof p === 'string' ? (
                            <span className="page-link">…</span>
                        ) : (
                            <Button onClick={() => handleClick(p)}>
                                {p + 1}
                            </Button>
                        )}
                    </li>
                ))}

                {/* mobile-only «текущая / всего» */}
                {isMobile && (
                    <li className="page-item active">
                        <span className="page-link">{page + 1} / {totalPages}</span>
                    </li>
                )}

                {/* Вперёд / → */}
                <li className={`page-item ${page === totalPages - 1 ? 'disabled' : ''}`}>
                    <Button disabled={page === totalPages - 1} onClick={() => handleClick(page + 1)}>
                        {isMobile ? '→' : 'Вперёд'}
                    </Button>
                </li>
            </ul>
        </nav>
    );
}
